import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import SEO from '../components/SEO';

const InvestorCharterPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Investor Charter | Client Rights | Equi Pulse Research"
        description="Investor Charter outlining rights and responsibilities of investors using Equi Pulse Research services as per SEBI Research Analyst regulations."
        url="/investor-charter"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-amber-400 mb-8">Investor Charter</h1>
        
        <div className="prose prose-invert prose-amber max-w-none text-gray-300">
          {/* Research Analyst Details */}
          <div className="bg-slate-800/50 border border-amber-500/20 p-6 rounded-lg mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Research Analyst Details</h2>
            <div className="space-y-2">
              <p><strong>Name:</strong> Equi Pulse</p>
              <p><strong>SEBI Registration No.:</strong> INH000024046</p>
              <p><strong>Registered Office:</strong> RE-54, Gyansila City, Dhabli, Indore (M.P.) – 453771</p>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-400" aria-hidden="true" />
                <a href="mailto:care@equipulseresearch.com" className="text-amber-400 hover:text-amber-300">
                  care@equipulseresearch.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-amber-400" aria-hidden="true" />
                <a href="tel:+918982892301" className="text-amber-400 hover:text-amber-300">
                  +91-8982892301
                </a>
              </div>
              <p><strong>Website:</strong> <a href="https://www.equipulseresearch.com" className="text-amber-400 hover:text-amber-300">www.equipulseresearch.com</a></p>
            </div>
          </div>

          {/* Vision */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Vision</h2>
            <p>To empower investors with transparent, unbiased, and research-driven insights for informed decision-making.</p>
          </div>

          {/* Mission */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Mission</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide high-quality research based on objective analysis</li>
              <li>Maintain transparency, compliance, and fairness in all dealings with investors</li>
              <li>Promote awareness of risks and responsibilities associated with securities markets</li>
              <li>Ensure responsible and ethical research practices</li>
            </ul>
          </div>

          {/* Services Provided */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Services Provided</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Equity research reports</li>
              <li>Technical analysis</li>
              <li>Fundamental analysis</li>
              <li>Sector & thematic research</li>
              <li>Long-term investment research</li>
              <li>Short-term trading ideas</li>
              <li>Model portfolios</li>
            </ul>
            <p className="mt-4 text-sm italic">
              <strong>Note:</strong> Equi Pulse provides research only and does not execute trades, manage funds, assure returns, or handle client money.
            </p>
          </div>

          {/* Investor Rights */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Investor Rights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-4 rounded-lg">
                <p>✓ Receive fair and unbiased research</p>
              </div>
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-4 rounded-lg">
                <p>✓ Be informed about disclosures and conflicts of interest</p>
              </div>
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-4 rounded-lg">
                <p>✓ Confidentiality of personal information</p>
              </div>
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-4 rounded-lg">
                <p>✓ Timely grievance redressal</p>
              </div>
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-4 rounded-lg">
                <p>✓ Access transparent and understandable research</p>
              </div>
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-4 rounded-lg">
                <p>✓ Right to discontinue services anytime</p>
              </div>
            </div>
          </div>

          {/* Investor Responsibilities - Do's */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Investor Responsibilities – Do's</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Deal only with SEBI-registered Research Analysts</li>
              <li>Read reports and disclosures carefully</li>
              <li>Understand market risks</li>
              <li>Make decisions based on personal financial situation</li>
              <li>Retain records of communication</li>
            </ul>
          </div>

          {/* Investor Responsibilities - Don'ts */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Investor Responsibilities – Don'ts</h2>
            <div className="bg-red-900/20 border border-red-500/30 p-6 rounded-lg">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Do not expect guaranteed returns</li>
                <li>Do not share confidential credentials</li>
                <li>Do not act on rumors</li>
                <li>Do not make cash payments</li>
              </ul>
            </div>
          </div>

          {/* Grievance Redressal */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Grievance Redressal</h2>
            <div className="bg-slate-800/50 border border-amber-500/20 p-6 rounded-lg">
              <p className="mb-4"><strong>Email:</strong> <a href="mailto:compliance@equipulseresearch.com" className="text-amber-400 hover:text-amber-300">compliance@equipulseresearch.com</a></p>
              <p className="mb-4"><strong>Acknowledgement:</strong> Within 5 business days</p>
              <p className="mb-4"><strong>Resolution:</strong> Within 15 business days (where possible)</p>
              <p><strong>SEBI SCORES:</strong> <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">https://scores.sebi.gov.in</a></p>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-900/20 border border-amber-500/30 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-amber-400 mb-4">Disclaimer</h2>
            <ul className="space-y-2">
              <li>• Investments are subject to market risks</li>
              <li>• Past performance does not guarantee future results</li>
              <li>• No assured returns</li>
              <li>• SEBI registration does not assure performance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorCharterPage;